/**
 * New Booking and the custom field admin, client side. CRM-BUILD 2026-09-15.
 *
 * ⚠️ `import type` FROM BOTH SERVICES, NEVER A VALUE IMPORT. Each imports the
 * database driver, and a value import would take Postgres into the browser
 * bundle - measured once at 98 modules to 225, and caught now by
 * `ops/check-bundle-boundary.sh`.
 */
import { reportUnauthorized } from './unauthorized.js'
import type { NewBookingOptions as ServerOptions } from '../../../src/services/new-booking.js'
import type { AdminGroup, AdminField } from '../../../src/services/field-admin.js'

export type NewBookingOptions = ServerOptions
export type FieldGroup = AdminGroup
export type FieldDefinition = AdminField

export type Outcome<T = void> =
  | ({ ok: true } & T)
  | { ok: false; reason: string; message?: string; given?: string }

async function call<T>(url: string, method: string, body?: unknown): Promise<Outcome<T>> {
  const res = await fetch(url, {
    method,
    credentials: 'same-origin',
    ...(body === undefined
      ? {}
      : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  })
  if (res.status === 401) {
    reportUnauthorized()
    return { ok: false, reason: 'unauthorized' }
  }
  const parsed = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (res.ok) return { ok: true, ...parsed } as Outcome<T>
  /** ⚠️ 409 CARRIES THE STRUCTURED REFUSAL with its numbers; 404 has only a
   *  status, so the reason is synthesised rather than invented per call site. */
  return {
    ok: false,
    reason: (parsed['reason'] as string) ?? (res.status === 404 ? 'contact-not-found' : 'unknown'),
    ...(parsed['message'] === undefined ? {} : { message: parsed['message'] as string }),
    ...(parsed['given'] === undefined ? {} : { given: parsed['given'] as string }),
  }
}

async function get<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin' })
  if (res.status === 401) {
    reportUnauthorized()
    throw new Error('unauthorized')
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as T
}

export async function fetchNewBookingOptions(): Promise<NewBookingOptions> {
  return await get<NewBookingOptions>('/api/bookings/new-options')
}

export async function createBooking(input: {
  contactReference: number
  pipelineId: string
  stageId: string
  title?: string
  value?: string
  fields?: Array<{ fieldId: string; value: string }>
}): Promise<Outcome<{ id: string; reference: number }>> {
  return await call<{ id: string; reference: number }>('/api/bookings', 'POST', input)
}

/* ─── The custom field admin ─────────────────────────────────────────────── */

export async function fetchFieldGroups(owner: 'contact' | 'booking'): Promise<FieldGroup[]> {
  return (await get<{ groups: FieldGroup[] }>(`/api/fields/${owner}`)).groups
}

export async function createFieldGroup(
  owner: 'contact' | 'booking',
  label: string,
): Promise<Outcome<{ id: string }>> {
  return await call<{ id: string }>(`/api/fields/${owner}/groups`, 'POST', { label })
}

export async function createFieldDefinition(
  groupId: string,
  input: { label: string; fieldType: string; required?: boolean; inDateSet?: boolean; options?: string[] },
): Promise<Outcome<{ id: string }>> {
  return await call<{ id: string }>(`/api/field-groups/${groupId}/fields`, 'POST', input)
}

export async function renameFieldDefinition(
  fieldId: string,
  input: { label?: string; required?: boolean; inDateSet?: boolean },
): Promise<Outcome> {
  return await call(`/api/fields/id/${fieldId}`, 'PATCH', input)
}

/** ⛔ Item 19's lifecycle, reachable from a screen for the first time. */
export async function setFieldRetired(fieldId: string, retired: boolean): Promise<Outcome> {
  return await call(`/api/fields/id/${fieldId}/retired`, 'POST', { retired })
}

/** ⛔ AND PURGE CAN BE REFUSED WITH ITS DEPENDENTS NAMED, which is item 19's own
 *  rule: it must not silently destroy a field a segment or a flow reads. */
export async function purgeFieldDefinition(fieldId: string): Promise<Outcome> {
  return await call(`/api/fields/id/${fieldId}`, 'DELETE')
}

export async function reorderFieldDefinitions(
  groupId: string,
  fieldIds: string[],
): Promise<Outcome> {
  return await call(`/api/field-groups/${groupId}/order`, 'PUT', { fieldIds })
}
